/**
 * User: Rob Richard
 * Date: 12/21/13
 */
"use strict";

var Backbone = require('dibs-backbone');
var $ = require('dibs-jquery-shim');
var _ = require('underscore');
var bunsenLibs = require('./bunsen');
var requ = require('reku');
var endpoints = require('dibs-endpoints');
var getDeferredMethods = require('../helpers/getDeferredMethods');
var log = require('bunsen/helpers/log');
var SV = require('server-vars');

// ---------------------------------------------------
// Local vars

var pathregex = /(.+?\:\/\/[^/]+)?(.*$)/;
var DEFAULT_TRANSPORT = requ;
var Deferred = getDeferredMethods().Deferred;
var globalAutoclumpForceDisable = SV.get('settings.enabler.global_autoclump_force_disable');
var Clump;
var globalAutoEnabled;
var backboneSync;
var clumpBackboneSync;

// ---------------------------------------------------
// Module Definition

/**
 * Clump library:
 *
 * Add a request using clump.add. This will not actually submit the http request
 * but add it to a queue. This function takes a subset of the same options you
 * would pass to $.ajax and returns a promise that is resolved when the request
 * is completed.
 *
 * @class Clump
 *
 * @example
 * // Create a new instance of a clumper
 * var clump = new Clump();
 *
 * promise1 = clump.add({
 *    url: ‘/soa/identity/user/123’,
 *    type: ‘GET’,
 *    data: { foo: bar }
 * });
 *
 * promise2 = clump.add({
 *    url: ‘/soa/identity/user/456’,
 *    type: ‘POST’,
 *    data: { foo: bar }
 * });
 *
 * // Actually submit the HTTP request now. Returns a promise that is resolved
 * // when the actual HTTP request is complete. promise1 and promise2 will be
 * // resolved/rejected when this completes based on the HTTP status from the
 * // clump service response
 * clumpPromise = clump.run();
 *
 */
globalAutoEnabled = function () {
    return !globalAutoclumpForceDisable && _.result(Clump.global, 'auto');
};
Clump = module.exports = function (options) {
    options = options || {};
    this.auto = options.auto || false;
    this.transport = options.transport || Clump.transport;
    this.url = endpoints.clump();
    this.queue = [];
    this._deferreds = [];
    this._requestOptions = [];
};
Clump.MODE_SEQ = 'SYNC_SEQ';
Clump.MODE_PARALLEL = 'SYNC_PARALLEL';

Clump.prototype = {
    clear: function () {
        this._deferreds.length = 0;
        this.queue.length = 0;
        this._requestOptions.length = 0;
    },
    /**
     * @param {function} transport
     */
    setTransport: function (transport) {
        this.transport = transport;
    },
    /**
     * @param {object} passedOptions
     * @returns {$.Deferred.promise}
     */
    run: function (passedOptions) {
        var options = passedOptions || {};
        var self = this;
        var deferreds = _.clone(this._deferreds);
        var xhr = new Deferred();
        var queue = _.clone(this.queue);
        var madeSingleRequest;
        var firstDeferred;
        var data;
        var requestDataJSON;

        _.defaults(options, {
            mode: 'SYNC_SEQ'
        });

        if (0 === _.size(queue)) {
            // Nothing to send so just return a resolved deferred
            xhr.resolve();
        } else if (1 === _.size(queue) && (false !== this.auto)) {
            // fall back to a non-clumped request if:
            //  - there is only one element in the clump queue, and
            //  - this clump is in auto mode
            firstDeferred = _.first(this._deferreds);
            madeSingleRequest = this._makeRequest(_.first(this._requestOptions));
            madeSingleRequest
                .done(function () {
                    firstDeferred.resolveWith(madeSingleRequest, arguments);
                })
                .fail(function () {
                    firstDeferred.rejectWith(madeSingleRequest, arguments);
                });
            xhr.resolve();
        } else {
            data = {
                mode: options.mode,
                abortOnError: (options.abortOnError === true || options.abortOnError === 'Y') ? 'Y' : 'N',
                elements: queue
            };

            if (options.dynaOptions) {
                data.dynaOptions = options.dynaOptions;
            }

            requestDataJSON = JSON.stringify(data);
            xhr = this._makeRequest({
                url: _.result(this, 'url'),
                data: requestDataJSON,
                type: 'post',
                dataType: 'json',
                contentType: 'application/json',
                context: this
            });

            xhr.done(function (returnedData) {
                _.each(returnedData.result.clumpResponse.elements, function (element, index) {
                    var responseURL = element.url;
                    var requestURL = queue[index].url;
                    var thisDeferred = deferreds[index];
                    var status = element.httpCode;
                    var badClump;
                    var errorMessage;
                    if ('?' === responseURL.substr(-1)) {
                        responseURL = responseURL.slice(0, -1);
                    }
                    if ('?' === requestURL.substr(-1)) {
                        requestURL = requestURL.slice(0, -1);
                    }
                    requestURL = pathregex.exec(requestURL)[2];
                    responseURL = pathregex.exec(responseURL)[2];
                    if (requestURL !== responseURL) {
                        errorMessage = "Clump expected to dispatch a response from " + requestURL + " but instead received a response from " + responseURL + ". Index was " + index + ".\n";
                        badClump = true;
                    } else if (queue[index].method && element.method !== queue[index].method) {
                        errorMessage = "Clump expected to dispatch a response for a " + queue[index].method + " request but was instead given a response to a " + element.method + " request. Index was " + index + ".";
                        badClump = true;
                    }
                    if (badClump) {
                        errorMessage += "Entire request from clump service was: " + requestDataJSON + "\n";
                        errorMessage += "Entire response from clump service was: " + JSON.stringify(returnedData) + "\n";
                        log.remote.error(errorMessage);
                        return thisDeferred.rejectWith(thisDeferred, [thisDeferred, "error", errorMessage]);
                    }
                    _.extend(thisDeferred, {
                        clumpIndex: index,
                        status: element.httpCode,
                        statusText: _.isObject(element.payload) ? element.payload.message : null,
                        responseJSON: _.isObject(element.payload) ? element.payload.result : null
                    });
                    // Only 200 error codes will resolve. 304 is technically not an error
                    // but we can't handle that.
                    if (status >= 200 && status < 300) {
                        thisDeferred.resolve(element.payload, element.message, thisDeferred);
                    } else {
                        _.extend(thisDeferred, {
                            statusText: "Error " + element.httpCode,
                            responseText: self._getResponseTextFromPayload(element.payload),
                            responseJSON: element.payload,
                            url: element.url,
                            type: element.method,
                            data: data.elements[index].payload
                        });
                        thisDeferred.rejectWith(thisDeferred, [thisDeferred, "error", thisDeferred.statusText]);
                    }
                }, self);
            }).fail(function (jqXHR) {
                // The clump service failed so reject all of the queued deferreds
                var xhrFailArgs = arguments;
                _.each(deferreds, function (thisDeferred) {
                    _.each([
                        'status',
                        'statusText',
                        'responseJSON',
                        'responseText'
                    ], function (k) {
                        thisDeferred[k] = jqXHR[k];
                    });
                    thisDeferred.reject.apply(thisDeferred, [thisDeferred].concat(Array.prototype.slice.call(xhrFailArgs, 1)));
                }, self);
            });
        }
        this.clear();

        return xhr;
    },
    /**
     * @param {object} settings
     *      jQuery.ajax style options
     * @returns {$.Deferred.promise}
     *      A promise for this specific config's
     *      resolved data
     */
    add: function (settings) {
        var deferred = new Deferred();
        var queueObj = {};
        var wait;

        // Invoke the static _beforeAddCallback function,
        // if present.
        if (_.isFunction(Clump._beforeAddCallback)) {
            settings = Clump._beforeAddCallback(settings) || settings;
        }


        if (this.auto) {
            wait = 0;
            if ("number" === typeof this.auto) {
                wait = this.auto;
            }
            _.delay(_.bind(this.run, this), wait);
        }
        queueObj.method = settings.type ? settings.type.toUpperCase() : 'GET';
        if (settings.clumpName) {
            queueObj.name = settings.clumpName;
        }
        if (settings.clumpProfile) {
            queueObj.profile = settings.clumpProfile;
        }

        if (queueObj.method === 'GET') {
            queueObj.url = bunsenLibs.extendURL(settings.url, settings.data, true);
        } else {
            queueObj.url = settings.url;
            queueObj.payload = settings.data;
        }

        this.queue.push(queueObj);
        this._deferreds.push(deferred);
        this._requestOptions.push(settings);

        // Support for $.ajax success, error, complete options
        if (settings.success) {
            deferred.done(settings.success);
        }
        if (settings.error) {
            deferred.fail(settings.error);
        }
        if (settings.complete) {
            deferred.always(settings.complete);
        }

        return deferred.promise();
    },
    _getResponseTextFromPayload: function (payload) {
        var text;
        if (_.isString(payload)) {
            return payload;
        }
        try {
            text = JSON.stringify(payload);
        } catch (e) {
            // do nothing
        }
        if (!text) {
            return payload;
        }
        return text;
    },
    _makeRequest: function (options) {
        return this.transport(options);
    }
};

Clump.transport = DEFAULT_TRANSPORT;

Clump.global = new Clump(); // set Clump.global.auto = true to use global clump implicitly via auto

/**
 * Resets the static transport property to the default
 */
Clump.resetTransport = function () {
    Clump.transport = DEFAULT_TRANSPORT;
};

/**
 * Sets the static transport propery temporarily, then resets it once the given
 * function is finished executing.
 * @param fn {function}
 * @param transport {function}
 */
Clump.executeWithTemporaryTransport = function (fn, transport) {
    Clump.transport = transport;
    $.when(fn()).then(function () {
        Clump.resetTransport();
    });
};

/**
 * Make a request with the global clump
 * instance (accessed through Clump.global)
 * @returns {$.Deferred.promise}
 */
Clump.ajax = function () { // use Clump.global to make a request if it exists and is set to auto, otherwise use $.ajax
    var clump;

    if (Clump.global && globalAutoEnabled()) {
        return Clump.global.add.apply(Clump.global, arguments);
    }

    clump = new Clump();
    return clump._makeRequest.apply(clump, arguments);
};

/**
 * Set a callback that is invoked every time
 * 'add' is called. Either through Backbone or
 * through manually adding to a clump instance.
 * You can do this to modify options for a
 * particular clump call element.
 * @param {function} callback
 * @return {function} Clump constructor
 */
Clump.setBeforeAddCallback = function (callback) {
    Clump._beforeAddCallback = callback;
    return Clump;
};

// TODO: remove the lines below that modify backbone directly and implement a system that doesn't rely on use side-effects requiring this package, e.g. middleware

backboneSync = Backbone.sync;

/**
 * Patch Backbone.sync to add clump support. Overrides Backbone.sync
 * and gives you the ability to add a 'clump' option to save/fetch calls.
 * @inner
 * @memberOf Clump
 * @param {string} method
 * @param {Backbone.Collection|Backbone.Model} model
 * @param {object} options
 * @return {$.Deferred.promise}
 *
 * @example
 * // Create a clump instance and some Backbone Objects
 * var clump = new Clump(),
 *     transactions = new bunsen.Transactions(), // Collection
 *     note = new bunsen.Note({content: ‘my content’}); // Model
 *
 * // pass the clump instance as an option to any of the
 * // backbone sync methods (save, fetch, destroy, etc)
 * transactions.fetch({clump: clump});
 * note.save(null, {clump: clump});
 *
 * // Request is actually submitted now. Sync events are triggered on
 * Backbone objects when this completes.
 * clump.run();
 *
 */
clumpBackboneSync = function (method, model, options) {
    var clump = options && options.clump;
    var xhr;
    var oldAjax;
    clump = clump || false !== clump && (globalAutoEnabled() ? Clump.global : false); // use global clump if no clump is passed, and auto is enabled for the global clump
    // If we are not using a clump, use backboneSync
    if (!clump) {
        return backboneSync.apply(this, arguments);
    }
    // Temporarily swap out Backbone ajax for clump.add
    oldAjax = Backbone.ajax;
    Backbone.ajax = _.bind(clump.add, clump);
    // Now call the original backbone sync
    xhr = backboneSync.apply(this, arguments);
    // Set Backbone.ajax back to the original value for the next call.
    Backbone.ajax = oldAjax;
    return xhr;
};


/**
 * Force clump to replace the clump version
 * of Backbone.sync with the custom one
 */
Clump.restoreOrigBackboneSync = function () {
    Backbone.sync = backboneSync;
};
/**
 * If using the original Backbone.sync function,
 * you can replace it with the clump Backbone.sync
 * function with this method.
 */
Clump.useClumpBackboneSync = function () {
    Backbone.sync = clumpBackboneSync;
};

// Use the clump backbone sync by default and override Backbone.sync
Clump.useClumpBackboneSync();



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/libraries/clump.js
// module id = 23
// module chunks = 64